/**
 * KpaNotifications - Gestion des notifications KPA Ton Concours
 * Tables : user_notifications, scheduled_notifications
 * Types : reminder, new_concours, deadline, info
 */
import {
  AlertCircle,
  Bell,
  CheckCircle,
  Clock,
  Loader2,
  MessageCircle,
  Plus,
  RefreshCw,
  Send,
  Smartphone,
  Trash2,
  UserX,
  X,
  Zap,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface ScheduledNotification {
  id: string;
  user_id?: string;
  title: string;
  body: string;
  notification_type: string;
  concours_id?: string;
  concours_title?: string;
  scheduled_at: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  sent_at?: string;
  error_message?: string;
  created_at: string;
}

interface NotificationStats {
  pending: number;
  sent: number;
  failed: number;
  total_today: number;
  users_with_push: number;
}

interface BroadcastForm {
  title: string;
  body: string;
  type: string;
  target: 'all' | 'active' | 'no_credits';
  scheduled_at: string;
  channel: 'push' | 'whatsapp';
}

const TYPE_STYLES: Record<string, string> = {
  reminder: 'bg-blue-100 text-blue-700',
  deadline: 'bg-red-100 text-red-700',
  deadline_reminder: 'bg-orange-100 text-orange-700',
  new_concours: 'bg-green-100 text-green-700',
  reengagement: 'bg-purple-100 text-purple-700',
  info: 'bg-gray-100 text-gray-600',
};

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const KpaNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<ScheduledNotification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showBroadcastForm, setShowBroadcastForm] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [reengageLoading, setReengageLoading] = useState(false);
  const [form, setForm] = useState<BroadcastForm>({
    title: '',
    body: '',
    type: 'info',
    target: 'all',
    scheduled_at: new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16),
    channel: 'push',
  });

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [notifRes, statsRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/pka/notifications/scheduled?limit=50`),
        authFetch(`${API_BASE_URL}/api/pka/notifications/stats`),
      ]);

      if (notifRes.ok) {
        const data = await notifRes.json();
        setNotifications(data.notifications ?? []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const cancelNotification = async (id: string) => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/pka/notifications/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erreur lors de l\'annulation');
      setMessage({ type: 'success', text: 'Notification annulée' });
      loadData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const triggerGenerateReminders = async () => {
    setRemindersLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/pka/notifications/generate-reminders`, { method: 'POST' });
      if (!res.ok) throw new Error('Erreur lors de la génération');
      const data = await res.json();
      setMessage({ type: 'success', text: `✅ ${data.deadline_reminders_created ?? 0} rappels deadline + ${data.reengagement_notifications_created ?? 0} relances créés` });
      loadData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setRemindersLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const triggerReengagement = async () => {
    setReengageLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/pka/notifications/reengagement`, { method: 'POST' });
      if (!res.ok) throw new Error('Erreur lors de la relance');
      const data = await res.json();
      setMessage({ type: 'success', text: `💬 ${data.reengagement_notifications_created ?? 0} relances programmées (utilisateurs inactifs 7j+)` });
      loadData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setReengageLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const sendBroadcast = async () => {
    if (!form.title || !form.body) {
      setMessage({ type: 'error', text: 'Titre et message requis' });
      return;
    }
    setBroadcastLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/pka/notifications/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Erreur lors de l\'envoi');
      const data = await res.json();
      setMessage({ type: 'success', text: `Notification programmée pour ${data.recipients ?? '?'} utilisateurs` });
      setShowBroadcastForm(false);
      setForm(f => ({ ...f, title: '', body: '' }));
      loadData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBroadcastLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-5 h-5 text-purple-500" />
            <h1 className="text-2xl font-bold text-gray-900">Notifications KPA</h1>
          </div>
          <p className="text-gray-500 text-sm">Rappels, deadlines, nouveaux concours — Push FCM + WhatsApp</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={triggerGenerateReminders}
            disabled={remindersLoading}
            title="Génère automatiquement les rappels pour les concours dont la deadline approche (1j/3j/7j) + relances inactifs"
            className="flex items-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition text-sm"
          >
            {remindersLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Générer rappels
          </button>
          <button
            onClick={triggerReengagement}
            disabled={reengageLoading}
            title="Relancer les utilisateurs inactifs depuis 7+ jours"
            className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-sm"
          >
            {reengageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
            Relancer inactifs
          </button>
          <button
            onClick={() => setShowBroadcastForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition text-sm"
          >
            <Plus className="w-4 h-4" />
            Broadcast
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'En attente', value: stats.pending },
            { label: 'Envoyées', value: stats.sent },
            { label: 'Échouées', value: stats.failed },
            { label: 'Aujourd\'hui', value: stats.total_today },
            { label: 'Users avec push', value: stats.users_with_push },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Types de notifications expliquées */}
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
        <h3 className="font-semibold text-purple-800 mb-2 text-sm">🔔 Types de notifications KPA</h3>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
          <div className="bg-blue-100 text-blue-700 rounded p-2"><strong>reminder</strong> — Rappel utilisateur (concours sauvegardé)</div>
          <div className="bg-red-100 text-red-700 rounded p-2"><strong>deadline</strong> — Clôture imminente</div>
          <div className="bg-orange-100 text-orange-700 rounded p-2"><strong>deadline_reminder</strong> — Auto-rappel 1j/3j/7j avant clôture</div>
          <div className="bg-green-100 text-green-700 rounded p-2"><strong>new_concours</strong> — Nouveau concours (catégorie préférée)</div>
          <div className="bg-purple-100 text-purple-700 rounded p-2"><strong>reengagement</strong> — Relance utilisateurs inactifs 7j+</div>
          <div className="bg-gray-100 text-gray-600 rounded p-2"><strong>info</strong> — Broadcast admin général</div>
        </div>
        <div className="mt-2 flex gap-3 text-xs text-purple-600">
          <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> Push FCM — nécessite FIREBASE_SERVICE_ACCOUNT dans les secrets Supabase</span>
          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp — nécessite WASENDER_API_KEY (déjà configuré)</span>
        </div>
      </div>

      {/* Notifications list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Notifications programmées</h2>
          <span className="text-sm text-gray-400">{notifications.length}</span>
        </div>
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Bell className="w-8 h-8 mx-auto mb-2" />
            <p>Aucune notification programmée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Notification</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Type</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Canal</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Statut</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Concours lié</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Programmée</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {notifications.map((n) => (
                  <tr key={n.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 text-sm">{n.title}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[220px]">{n.body}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_STYLES[n.notification_type] ?? 'bg-gray-100 text-gray-600'}`}>
                        {n.notification_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(n as any).channel === 'whatsapp' ? (
                        <span className="flex items-center justify-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-green-50 text-green-600">
                          <MessageCircle className="w-3 h-3" /> WA
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-600">
                          <Smartphone className="w-3 h-3" /> Push
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[n.status]}`}>
                        {n.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[140px] truncate">{n.concours_title || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(n.scheduled_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {n.status === 'pending' && (
                        <button
                          onClick={() => cancelNotification(n.id)}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded transition"
                          title="Annuler"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Broadcast form modal */}
      {showBroadcastForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-bold text-gray-900">Envoyer une notification</h2>
              <button onClick={() => setShowBroadcastForm(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Nouveau concours disponible !"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
                  placeholder="Le CAFOP 2026 est ouvert aux inscriptions..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="info">info</option>
                    <option value="new_concours">new_concours</option>
                    <option value="deadline">deadline</option>
                    <option value="reminder">reminder</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cible</label>
                  <select
                    value={form.target}
                    onChange={(e) => setForm(f => ({ ...f, target: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">Tous les utilisateurs</option>
                    <option value="active">Actifs (7j)</option>
                    <option value="no_credits">Sans crédits</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Canal d'envoi</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="channel"
                      value="push"
                      checked={form.channel === 'push'}
                      onChange={() => setForm(f => ({ ...f, channel: 'push' }))}
                      className="accent-blue-600"
                    />
                    <Smartphone className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700">Push FCM (app mobile)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="channel"
                      value="whatsapp"
                      checked={form.channel === 'whatsapp'}
                      onChange={() => setForm(f => ({ ...f, channel: 'whatsapp' }))}
                      className="accent-green-600"
                    />
                    <MessageCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">WhatsApp (numéro vérifié)</span>
                  </label>
                </div>
                {form.channel === 'push' && (
                  <p className="text-xs text-gray-400 mt-1">⚠️ Requiert FIREBASE_SERVICE_ACCOUNT dans les secrets Supabase</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Programmée le</label>
                <input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowBroadcastForm(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={sendBroadcast}
                  disabled={broadcastLoading}
                  className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
                >
                  {broadcastLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Programmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KpaNotifications;
